import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Input } from '@pantry/ui';
import {
  useProductGroup,
  useUpdateProductGroup,
  useDeleteProductGroup,
  useAddGroupMember,
  useRemoveGroupMember,
  useResyncGroup,
  useUnifiedSearch,
  type ProductGroupSummary,
  type ProductGroupMemberRow,
} from '@pantry/api-client';
import {
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  ScissorsIcon,
  ArrowsRightLeftIcon,
  PencilIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { SyncBadge } from './SyncBadge';
import { SplitGroupDialog } from './SplitGroupDialog';
import { MergeGroupDialog } from './MergeGroupDialog';

interface GroupDetailPanelProps {
  householdId: string;
  group: ProductGroupSummary;
  allGroups: ProductGroupSummary[];
  onClose: () => void;
}

/**
 * Detail panel for a product group. Shows members, sync status, and actions.
 */
export function GroupDetailPanel({
  householdId,
  group,
  allGroups,
  onClose,
}: GroupDetailPanelProps) {
  const { t } = useLingui();

  // Track whether user has dismissed the "editing will diverge" warning this session
  const [editWarningDismissed, setEditWarningDismissed] = useState(false);
  const [showEditWarning, setShowEditWarning] = useState(false);

  // Member add search state
  const [addSearchQuery, setAddSearchQuery] = useState('');
  const [showAddSearch, setShowAddSearch] = useState(false);

  // Rename state
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameName, setRenameName] = useState(group.name);

  // Dialog state
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const { data: detail, isLoading } = useProductGroup(householdId, group.id);
  const updateGroup = useUpdateProductGroup();
  const deleteGroup = useDeleteProductGroup();
  const addMember = useAddGroupMember();
  const removeMember = useRemoveGroupMember();
  const resyncGroup = useResyncGroup();

  const { products: searchProducts } = useUnifiedSearch(householdId, addSearchQuery);

  const isDiverged = group.syncStatus === 'diverged';
  const isSynced = group.syncStatus === 'synced';

  const requireEditWarning = (action: () => void) => {
    if (isSynced && !editWarningDismissed) {
      setShowEditWarning(true);
      // Store the pending action — we'll invoke it on "Continue Editing"
      setPendingAction(() => action);
    } else {
      action();
    }
  };

  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const handleContinueEditing = () => {
    setEditWarningDismissed(true);
    setShowEditWarning(false);
    pendingAction?.();
    setPendingAction(null);
  };

  const handleCancelEditWarning = () => {
    setShowEditWarning(false);
    setPendingAction(null);
  };

  const handleResync = async () => {
    try {
      await resyncGroup.mutateAsync({ householdId, groupId: group.id });
      toast.success(t`Group resynced with defaults`);
      onClose();
    } catch {
      toast.error(t`Failed to resync group`);
    }
  };

  const handleRenameSubmit = async () => {
    if (!renameName.trim() || renameName.trim() === group.name) {
      setIsRenaming(false);
      return;
    }
    try {
      await updateGroup.mutateAsync({
        householdId,
        groupId: group.id,
        data: { name: renameName.trim() },
      });
      toast.success(t`Group renamed`);
      setIsRenaming(false);
    } catch {
      toast.error(t`Failed to rename group`);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteGroup.mutateAsync({ householdId, groupId: group.id });
      toast.success(t`Group deleted`);
      onClose();
    } catch {
      toast.error(t`Failed to delete group`);
    }
  };

  const handleAddMember = async (productId: string, productName: string) => {
    try {
      await addMember.mutateAsync({ householdId, groupId: group.id, productId });
      toast.success(t`"${productName}" added to group`);
      setAddSearchQuery('');
      setShowAddSearch(false);
    } catch {
      toast.error(t`Failed to add member`);
    }
  };

  const handleRemoveMember = async (member: ProductGroupMemberRow) => {
    try {
      await removeMember.mutateAsync({
        householdId,
        groupId: group.id,
        productId: member.productId,
      });
      toast.success(t`"${member.productName ?? 'Product'}" removed from group`);
    } catch {
      toast.error(t`Failed to remove member`);
    }
  };

  const members = detail?.members ?? [];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 pb-4 border-b border-border/50">
        <div className="flex-1 min-w-0">
          {isRenaming ? (
            <div className="flex items-center gap-2">
              <Input
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                className="rounded-xl font-display text-lg font-semibold h-9"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleRenameSubmit();
                  if (e.key === 'Escape') {
                    setIsRenaming(false);
                    setRenameName(group.name);
                  }
                }}
              />
              <Button
                size="icon"
                variant="ghost"
                className="rounded-xl h-9 w-9 text-primary"
                onClick={() => void handleRenameSubmit()}
                disabled={updateGroup.isPending}
              >
                <CheckIcon className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              {group.icon && <span className="text-xl">{group.icon}</span>}
              <h2 className="font-display text-lg font-semibold text-foreground truncate">
                {group.name}
              </h2>
              <SyncBadge status={group.syncStatus} />
            </div>
          )}
          {group.category && (
            <p className="text-sm text-muted-foreground mt-0.5">{group.category}</p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="rounded-xl h-9 w-9 flex-shrink-0 text-muted-foreground hover:text-foreground"
        >
          <XMarkIcon className="h-5 w-5" />
        </Button>
      </div>

      {/* Diverged banner */}
      {isDiverged && (
        <div className="mt-4 rounded-lg p-4 bg-sync-diverged/5 border border-sync-diverged/20">
          <p className="text-sm text-sync-diverged font-medium">
            <Trans>This group has been edited and is no longer synced with system defaults.</Trans>
          </p>
          {group.divergedAt && (
            <p className="text-xs text-muted-foreground mt-1">
              <Trans>
                Edited on{' '}
                {new Date(group.divergedAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </Trans>
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void handleResync()}
            disabled={resyncGroup.isPending}
            className="mt-3 rounded-xl h-8 text-sync-diverged border-sync-diverged/30 hover:bg-sync-diverged/10"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 mr-1.5" />
            <Trans context="action_button">Resync with Defaults</Trans>
          </Button>
        </div>
      )}

      {/* Edit warning (synced group) */}
      {showEditWarning && (
        <div className="mt-4 rounded-lg p-4 bg-muted/50 border border-border">
          <p className="text-sm text-foreground font-medium">
            <Trans>
              Editing this group will disconnect it from system defaults. You can always resync
              later.
            </Trans>
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              variant="default"
              size="sm"
              onClick={handleContinueEditing}
              className="rounded-xl h-8"
            >
              <Trans context="action_button">Continue Editing</Trans>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelEditWarning}
              className="rounded-xl h-8"
            >
              <Trans context="action_button">Cancel</Trans>
            </Button>
          </div>
        </div>
      )}

      {/* Members section */}
      <div className="flex-1 overflow-y-auto mt-4 space-y-1">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-foreground">
            <Trans>Members</Trans>
            <span className="ml-1.5 text-muted-foreground font-normal">
              ({isLoading ? '…' : members.length})
            </span>
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              requireEditWarning(() => {
                setShowAddSearch((prev) => !prev);
              })
            }
            className="rounded-xl h-7 text-xs text-primary hover:bg-primary/10"
          >
            <PlusIcon className="h-3.5 w-3.5 mr-1" />
            <Trans context="action_button">Add</Trans>
          </Button>
        </div>

        {/* Add member search */}
        {showAddSearch && (
          <div className="mb-3 space-y-2">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t`Search products…`}
                value={addSearchQuery}
                onChange={(e) => setAddSearchQuery(e.target.value)}
                className="pl-9 rounded-xl"
                autoFocus
              />
            </div>
            {addSearchQuery.length >= 2 && searchProducts.length > 0 && (
              <div className="rounded-xl border border-border bg-background shadow-sm max-h-40 overflow-y-auto">
                {searchProducts.slice(0, 10).map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => void handleAddMember(product.id, product.name ?? '')}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-muted/50 transition-colors first:rounded-t-xl last:rounded-b-xl"
                  >
                    {product.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-9 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : members.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            <Trans>No products in this group yet.</Trans>
          </p>
        ) : (
          <ul className="space-y-1">
            {members.map((member) => (
              <li
                key={member.id}
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl hover:bg-muted/40 group transition-colors"
              >
                <span className="text-sm text-foreground truncate">
                  {member.productName ?? member.productId}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() =>
                    requireEditWarning(() => {
                      void handleRemoveMember(member);
                    })
                  }
                  className="rounded-xl h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  disabled={removeMember.isPending}
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Action buttons */}
      <div className="pt-4 border-t border-border/50 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              requireEditWarning(() => {
                setRenameName(group.name);
                setIsRenaming(true);
              })
            }
            className="rounded-xl h-9"
          >
            <PencilIcon className="h-3.5 w-3.5 mr-1.5" />
            <Trans context="action_button">Rename</Trans>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSplitDialog(true)}
            className="rounded-xl h-9"
            disabled={members.length < 2}
          >
            <ScissorsIcon className="h-3.5 w-3.5 mr-1.5" />
            <Trans context="action_button">Split</Trans>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMergeDialog(true)}
            className="rounded-xl h-9"
            disabled={allGroups.length < 2}
          >
            <ArrowsRightLeftIcon className="h-3.5 w-3.5 mr-1.5" />
            <Trans context="action_button">Merge</Trans>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
            className="rounded-xl h-9 text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive"
          >
            <TrashIcon className="h-3.5 w-3.5 mr-1.5" />
            <Trans context="action_button">Delete</Trans>
          </Button>
        </div>
      </div>

      {/* Delete confirm inline */}
      {showDeleteConfirm && (
        <div className="mt-2 rounded-xl bg-destructive/5 border border-destructive/20 p-3 space-y-3">
          <p className="text-sm text-destructive font-medium">
            <Trans>Delete &ldquo;{group.name}&rdquo;? This cannot be undone.</Trans>
          </p>
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => void handleDelete()}
              disabled={deleteGroup.isPending}
              className="rounded-xl h-8"
            >
              {deleteGroup.isPending ? <Trans>Deleting…</Trans> : <Trans>Delete</Trans>}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteConfirm(false)}
              className="rounded-xl h-8"
            >
              <Trans context="action_button">Cancel</Trans>
            </Button>
          </div>
        </div>
      )}

      {/* Split dialog */}
      {showSplitDialog && detail && (
        <SplitGroupDialog
          householdId={householdId}
          group={group}
          members={members}
          open={showSplitDialog}
          onOpenChange={setShowSplitDialog}
          onSuccess={onClose}
        />
      )}

      {/* Merge dialog */}
      {showMergeDialog && (
        <MergeGroupDialog
          householdId={householdId}
          sourceGroup={group}
          allGroups={allGroups.filter((g) => g.id !== group.id)}
          open={showMergeDialog}
          onOpenChange={setShowMergeDialog}
          onSuccess={onClose}
        />
      )}
    </div>
  );
}
